import {AlertTriangleIcon, ArrowDownIcon, ArrowUpIcon, CheckCircle2Icon, ChevronDownIcon, ChevronRightIcon, CircleAlertIcon, Loader2Icon} from "lucide-react";
import {useState} from "react";

import {Badge} from "@components/ui/badge";
import {Card, CardContent} from "@components/ui/card";
import {Skeleton} from "@components/ui/skeleton";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@components/ui/table";
import {
    ModelingWorkbenchData,
    ModelingWorkbenchKpi,
    ModelingWorkbenchRow,
    ModelingWorkbenchStatement,
    ThreeStatementCheck,
    useVNextModelingWorkbench,
} from "@hooks/use-vnext";
import {cn} from "@lib/utils";

import {StickyHorizontalScrollFrame} from "./StickyHorizontalScrollFrame";


interface ForecastSummaryTabProps {
    workspaceId: string;
    scenarioId: string;
    actualMonths?: number;
    forecastMonths?: number;
}

function normalizeMetricNumber(value: unknown): number | null {
    if (typeof value === "number" && Number.isFinite(value)) {
        return Object.is(value, -0) || Math.abs(value) < 0.005 ? 0 : value;
    }
    if (typeof value === "string" && value.trim().length > 0) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return Object.is(parsed, -0) || Math.abs(parsed) < 0.005 ? 0 : parsed;
        }
    }
    return null;
}

function formatCompactCurrency(value: number): string {
    if (Math.abs(value) < 0.5) return "$0";
    const abs = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
    return `${sign}$${abs.toFixed(0)}`;
}

function formatMetricValue(value: unknown, format: ModelingWorkbenchKpi["format"]): string {
    const normalized = normalizeMetricNumber(value);
    if (normalized == null) return "—";
    if (format === "percent") return `${normalized.toFixed(1)}%`;
    if (format === "months") return normalized > 999 ? "Positive" : `${normalized.toFixed(1)} mo`;
    return formatCompactCurrency(normalized);
}

function findLastForecastBalanceCheck(statement: ModelingWorkbenchStatement | undefined): number | null {
    if (!statement) return null;
    const lastForecastMonth = [...statement.months].reverse().find((month) => month.status === "forecast");
    if (!lastForecastMonth) return null;
    const row = statement.rows.find((candidate) =>
        candidate.node_id === "__bs_balance_check__"
        || candidate.node_id === "balance_check"
        || candidate.label === "Balance Check"
        || candidate.category_key === "balance_check"
        || candidate.category_key === "system.balance_check");
    return normalizeMetricNumber(row?.values?.[lastForecastMonth.month_key]?.value);
}

function buildDisplayKpis(
    kpis: ModelingWorkbenchKpi[],
    bsStatement?: ModelingWorkbenchStatement,
): ModelingWorkbenchKpi[] {
    const fallbackBalanceCheck = findLastForecastBalanceCheck(bsStatement);
    return kpis.map((kpi) => {
        const normalized = normalizeMetricNumber(kpi.value);
        if (kpi.key === "balance_check") {
            return {...kpi, value: normalized ?? fallbackBalanceCheck};
        }
        return {...kpi, value: normalized};
    });
}

function visibleKpis(kpis: ModelingWorkbenchKpi[]) {
    return kpis.filter((kpi) => kpi.value != null);
}

const UP_IS_BAD_KPI_KEYS = new Set(["cogs", "opex"]);

function ForecastVsBaseIndicator({kpi, hasScenarioOps}: {kpi: ModelingWorkbenchKpi; hasScenarioOps: boolean}) {
    if (!hasScenarioOps) return null;
    if (kpi.baseline == null || kpi.value == null) return null;
    const diff = kpi.value - kpi.baseline;
    if (Math.abs(diff) < 0.05) return null;

    const pct = kpi.baseline !== 0 ? ((diff / Math.abs(kpi.baseline)) * 100) : null;
    const isUp = diff > 0;
    const inverted = UP_IS_BAD_KPI_KEYS.has(kpi.key);
    const isGood = inverted ? !isUp : isUp;
    const color = isGood ? "text-emerald-600" : "text-rose-600";

    const formatted = kpi.format === "percent"
        ? `${diff > 0 ? "+" : ""}${diff.toFixed(1)}pp`
        : `${diff > 0 ? "+" : ""}${formatCompactCurrency(diff)}`;

    return (
        <div className={`mt-0.5 flex items-center gap-0.5 ${color}`}>
            {isUp ? <ArrowUpIcon className="size-2.5" /> : <ArrowDownIcon className="size-2.5" />}
            <span className="text-[10px] font-medium">
                {formatted}
                {pct != null && ` (${pct > 0 ? "+" : ""}${pct.toFixed(0)}%)`}
            </span>
            <span className="text-[10px] text-slate-400 ml-0.5">vs base</span>
        </div>
    );
}

function TopKpiStrip({kpis, hasScenarioOps}: {kpis: ModelingWorkbenchKpi[]; hasScenarioOps: boolean}) {
    const cards = visibleKpis(kpis);
    return (
        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-3 shadow-sm">
            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                {cards.map((kpi) => (
                    <Card key={kpi.key} className="border border-slate-200 shadow-none">
                        <CardContent className="p-2.5">
                            <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                            <p className="mt-0.5 text-base font-semibold leading-tight text-slate-950">{formatMetricValue(kpi.value, kpi.format)}</p>
                            <ForecastVsBaseIndicator kpi={kpi} hasScenarioOps={hasScenarioOps} />
                            <p className="mt-0.5 text-[10px] text-slate-400">{kpi.period.replace(/_/g, " ")}</p>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </section>
    );
}

function formatCellValue(value: number, categoryKey: string): string {
    const key = categoryKey.toLowerCase();
    const keyParts = key.split(/[^a-z0-9]+/).filter(Boolean);
    if (keyParts.some((part) => ["pct", "percent", "percentage", "margin", "ratio"].includes(part))) {
        return `${value.toFixed(1)}%`;
    }
    return `$${value.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
}

function getForecastStartIndex(months: ModelingWorkbenchStatement["months"]): number {
    const index = months.findIndex((month) => month.status === "forecast");
    return index >= 0 ? index : months.length;
}

function statusBadgeClass(status: ThreeStatementCheck["status"]): string {
    if (status === "PASS") return "bg-green-100 text-green-700";
    if (status === "WARN") return "bg-amber-100 text-amber-700";
    return "bg-red-100 text-red-700";
}

function StatusIcon({status}: {status: ThreeStatementCheck["status"]}) {
    if (status === "PASS") return <CheckCircle2Icon className="size-4 text-green-600" />;
    if (status === "WARN") return <AlertTriangleIcon className="size-4 text-amber-600" />;
    return <CircleAlertIcon className="size-4 text-red-600" />;
}

function countStatuses(checks: ThreeStatementCheck[]) {
    return checks.reduce(
        (acc, check) => {
            acc[check.status] += 1;
            return acc;
        },
        {PASS: 0, WARN: 0, FAIL: 0},
    );
}

function rowTone(row: ModelingWorkbenchRow): string {
    if (row.row_type === "section_header") return "bg-slate-50";
    if (row.row_type === "section_total") return "bg-slate-50/60";
    if (row.row_type === "computed" || row.row_type === "synthetic") return "bg-slate-50/40";
    return "";
}

function rowLabelClass(row: ModelingWorkbenchRow): string {
    if (row.row_type === "section_header") {
        return "text-[11px] font-semibold uppercase tracking-[1.5px] text-slate-500";
    }
    if (row.is_bold || row.row_type === "section_total" || row.row_type === "computed" || row.row_type === "synthetic") {
        return "text-sm font-semibold text-slate-900";
    }
    return "text-sm font-medium text-slate-800";
}

function StatementPackSection({statement}: {statement: ModelingWorkbenchStatement}) {
    const actualCount = statement.months.filter((month) => month.status === "actual").length;
    const forecastCount = statement.months.length - actualCount;
    const forecastStartIndex = getForecastStartIndex(statement.months);

    return (
        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
            <div>
                <h3 className="text-sm font-semibold text-slate-900">{statement.label}</h3>
                <p className="mt-1 text-xs text-slate-500">{actualCount} actual + {forecastCount} forecast months</p>
            </div>

            <StickyHorizontalScrollFrame className="mt-4" viewportClassName="max-h-[36rem] overflow-y-auto pr-1">
                <div className="rounded-xl border border-slate-200">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50">
                                <TableHead className="sticky left-0 top-0 z-30 min-w-[280px] bg-slate-50">Line Item</TableHead>
                                {statement.months.map((month, monthIndex) => (
                                    <TableHead
                                        key={`${statement.key}-${month.month_key}`}
                                        className={cn(
                                            "sticky top-0 z-20 min-w-[124px] text-right",
                                            month.status === "forecast" ? "bg-amber-50/70" : "bg-slate-50",
                                            monthIndex === forecastStartIndex && "border-l-2 border-l-amber-300",
                                        )}
                                    >
                                        <div className="flex items-center justify-end gap-2">
                                            <span>{month.label}</span>
                                            <Badge
                                                variant="outline"
                                                className={month.status === "forecast"
                                                    ? "border-amber-300 bg-amber-100 text-[10px] text-amber-700"
                                                    : "border-teal-300 bg-teal-50 text-[10px] text-teal-700"}
                                            >
                                                {month.status === "forecast" ? "forecast" : "actual"}
                                            </Badge>
                                        </div>
                                    </TableHead>
                                ))}
                                <TableHead className="sticky right-0 top-0 z-30 min-w-[140px] bg-slate-50 text-right">Total</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {statement.rows.map((row) => (
                                <TableRow key={`${statement.key}-${row.node_id}`} className={cn("hover:bg-slate-50/80", rowTone(row))}>
                                    <TableCell className="sticky left-0 z-10 bg-white align-top">
                                        <div style={{paddingLeft: `${row.depth * 18}px`}}>
                                            <p className={rowLabelClass(row)}>{row.label}</p>
                                        </div>
                                    </TableCell>
                                    {statement.months.map((month, monthIndex) => {
                                        const cell = row.values[month.month_key];
                                        return (
                                            <TableCell
                                                key={`${row.node_id}-${month.month_key}`}
                                                className={cn(
                                                    "text-right tabular-nums text-xs",
                                                    month.status === "forecast" ? "bg-amber-50/30" : "",
                                                    monthIndex === forecastStartIndex && "border-l-2 border-l-amber-200",
                                                )}
                                            >
                                                <span className={cn(
                                                    row.is_bold || row.row_type === "section_total" || row.row_type === "computed" || row.row_type === "synthetic"
                                                        ? "font-semibold text-slate-900"
                                                        : "text-slate-700",
                                                )}>
                                                    {cell ? formatCellValue(cell.value, row.category_key) : "—"}
                                                </span>
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell className="sticky right-0 z-10 bg-white text-right tabular-nums text-xs">
                                        <span className={cn(
                                            row.is_bold || row.row_type === "section_total" || row.row_type === "computed" || row.row_type === "synthetic"
                                                ? "font-semibold text-slate-900"
                                                : "text-slate-700",
                                        )}>
                                            {/opening_cash|closing_cash/.test(row.node_id) ? "" : formatCellValue(row.total, row.category_key)}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </StickyHorizontalScrollFrame>
        </section>
    );
}

function ChecksSection({checks}: {checks: ThreeStatementCheck[]}) {
    const counts = countStatuses(checks);
    const hasIssue = counts.FAIL > 0 || counts.WARN > 0;
    const [isOpen, setIsOpen] = useState(hasIssue);
    const ChevIcon = isOpen ? ChevronDownIcon : ChevronRightIcon;
    return (
        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
            <button
                type="button"
                onClick={() => setIsOpen((open) => !open)}
                className="flex w-full flex-wrap items-center justify-between gap-3 text-left"
            >
                <div className="flex items-center gap-2">
                    <ChevIcon className="size-4 text-slate-500" />
                    <h3 className="text-sm font-semibold text-slate-900">Engine Checks</h3>
                </div>
                <div className="flex gap-2">
                    <Badge className={statusBadgeClass("PASS")}>Pass {counts.PASS}</Badge>
                    {counts.WARN > 0 && <Badge className={statusBadgeClass("WARN")}>Warn {counts.WARN}</Badge>}
                    {counts.FAIL > 0 && <Badge className={statusBadgeClass("FAIL")}>Fail {counts.FAIL}</Badge>}
                </div>
            </button>

            {isOpen && (
                <div className="mt-4 space-y-2">
                    {checks.length > 0 ? checks.map((check) => (
                        <div key={`${check.key}-${check.month_key ?? "all"}`} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3">
                            <div className="flex items-start gap-3">
                                <StatusIcon status={check.status} />
                                <div>
                                    <p className="text-sm font-medium text-slate-900">{check.label}</p>
                                    <p className="text-xs text-slate-500">
                                        {check.statement ? check.statement.toUpperCase() : "SYSTEM"}
                                        {check.month_key ? ` • ${check.month_key}` : ""}
                                    </p>
                                    {(check.reason || check.details) && (
                                        <p className="mt-1 text-xs text-slate-600">{check.reason ?? check.details}</p>
                                    )}
                                </div>
                            </div>
                            {(check.delta != null || check.threshold != null) && (
                                <div className="text-right text-xs text-slate-500">
                                    {check.delta != null && <p>Δ {formatMetricValue(check.delta, "currency")}</p>}
                                    {check.threshold != null && <p>Threshold {formatMetricValue(check.threshold, "currency")}</p>}
                                </div>
                            )}
                        </div>
                    )) : (
                        <div className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                            No engine checks returned.
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

function WarningsSection({data}: {data: ModelingWorkbenchData}) {
    const activeRevisions = data.ops_history.filter((op) => op.is_active).length;
    const hasContent = data.warnings.length > 0 || activeRevisions > 0;
    // Auto-open only for truly critical state — ERROR/CRITICAL-level warnings.
    // Routine WARN-level data-completeness warnings (e.g. "only X/12 months
    // available for avg_12m") and active revisions alone should NOT pop the
    // panel open; users can expand manually if interested.
    const hasCriticalWarning = data.warnings.some((w) => {
        const level = (w.level ?? "").toUpperCase();
        return level === "ERROR" || level === "CRITICAL";
    });
    const [isOpen, setIsOpen] = useState(hasCriticalWarning);
    const ChevIcon = isOpen ? ChevronDownIcon : ChevronRightIcon;
    return (
        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
            <button
                type="button"
                onClick={() => setIsOpen((open) => !open)}
                className="flex w-full flex-wrap items-center justify-between gap-3 text-left"
            >
                <div className="flex items-center gap-2">
                    <ChevIcon className="size-4 text-slate-500" />
                    <h3 className="text-sm font-semibold text-slate-900">Model Notes</h3>
                </div>
                <div className="flex gap-2">
                    {activeRevisions > 0 && <Badge variant="outline" className="text-xs">{activeRevisions} active revisions</Badge>}
                    {data.warnings.length > 0 && <Badge variant="outline" className="text-xs">{data.warnings.length} warnings</Badge>}
                    {!hasContent && <Badge variant="outline" className="text-xs text-slate-500">No notes</Badge>}
                </div>
            </button>

            {isOpen && (
                <div className="mt-4 space-y-2">
                    {data.warnings.length > 0 ? data.warnings.map((warning, idx) => (
                        <div key={`${warning.code ?? "warning"}-${idx}`} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                            <p className="font-medium">{warning.message}</p>
                            <p className="text-xs text-amber-700">
                                {(warning.level ?? "WARN").toUpperCase()}
                                {warning.statement ? ` • ${warning.statement.toUpperCase()}` : ""}
                                {warning.month_key ? ` • ${warning.month_key}` : ""}
                            </p>
                        </div>
                    )) : (
                        <div className="rounded-lg border border-dashed border-slate-300 p-3 text-sm text-slate-500">
                            No model warnings returned.
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

export function ForecastSummaryTab({
    workspaceId,
    scenarioId,
    actualMonths = 12,
    forecastMonths = 12,
}: ForecastSummaryTabProps) {
    const {data, isLoading, error} = useVNextModelingWorkbench(workspaceId, scenarioId, forecastMonths, actualMonths);

    if (isLoading) {
        return (
            <div className="space-y-6 p-4">
                <Skeleton className="h-28 w-full rounded-2xl" />
                <div className="grid gap-4 lg:grid-cols-3">
                    {Array.from({length: 3}).map((_, idx) => <Skeleton key={idx} className="h-40 rounded-2xl" />)}
                </div>
                <Skeleton className="h-64 w-full rounded-2xl" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-full min-h-[320px] items-center justify-center p-6">
                <div className="max-w-xl rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center shadow-sm">
                    <Loader2Icon className="mx-auto mb-3 size-5 text-slate-400" />
                    <h3 className="text-sm font-semibold text-slate-900">Forecast Summary unavailable</h3>
                    <p className="mt-2 text-sm text-slate-500">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) return null;
    const displayKpis = buildDisplayKpis(data.kpis, data.statements.bs);

    return (
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto bg-[#F8FAFC] p-4 pb-8">
            <div className="mx-auto w-full max-w-[1600px] space-y-6">
                <header className="rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#1E293B] p-6 shadow-sm">
                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-[#93C5FD]">
                        Pecunio | Forecast Summary
                    </p>
                    <div className="mt-3">
                        <h1 className="text-2xl font-semibold text-white">{data.meta.scenario.name}</h1>
                        <p className="mt-1 text-sm text-slate-200">
                            {data.meta.n_actual_months} actual months + {data.meta.n_forecast_months} forecast months
                        </p>
                    </div>
                </header>

                <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                        <AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-amber-600" />
                        <div>
                            <p className="text-sm font-semibold text-amber-900">
                                BETA — Forecast engine in active stabilisation
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-amber-800">
                                This view is an internal preview of the integrated 3-statement
                                forecast engine. Engine reconciliation checks below indicate the
                                current model-trust level on each statement. Cross-check against
                                actuals before relying on any forecast figure for external commitments.
                            </p>
                        </div>
                    </div>
                </div>

                <TopKpiStrip kpis={displayKpis} hasScenarioOps={data.ops_history.some((op) => op.is_active)} />

                <StatementPackSection statement={data.statements.pnl} />
                <StatementPackSection statement={data.statements.bs} />
                <StatementPackSection statement={data.statements.cf} />

                <ChecksSection checks={data.checks} />
                <WarningsSection data={data} />
            </div>
        </div>
    );
}
